import React from 'react';
import { useTranslation } from 'react-i18next';
import { LegalPageLayout } from './LegalPageLayout';
import { FaCookieBite } from 'react-icons/fa';

/* ── Reusable sub-components ── */

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h2 className="section-title">{children}</h2>;

const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="info-box">{children}</div>
);

const FeatureCard: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="feature-card">
    <h3 className="text-white font-semibold mb-2">{title}</h3>
    <p className="text-sm text-gray-400">{children}</p>
  </div>
);

const TypeCard: React.FC<{
  title: string;
  colorClass: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}> = ({ title, colorClass, children, extra }) => (
  <div className="feature-card">
    <h3 className={`text-lg font-semibold ${colorClass}`}>{title}</h3>
    <p className="text-sm text-gray-400 mt-2">{children}</p>
    {extra}
  </div>
);

/* ── Page ── */

export const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    { id: 'what', title: t('cookiePolicy.sections.what.title') },
    { id: 'how', title: t('cookiePolicy.sections.how.title') },
    { id: 'types', title: t('cookiePolicy.sections.types.title') },
    { id: 'managing', title: t('cookiePolicy.sections.managing.title') },
    { id: 'changes', title: t('cookiePolicy.sections.changes.title') },
  ];

  return (
    <LegalPageLayout
      icon={<FaCookieBite />}
      title={t('cookiePolicy.title')}
      lastUpdated={t('cookiePolicy.lastUpdated')}
      contentsLabel={t('cookiePolicy.contents')}
      sections={sections}
    >
      {(activeSection) => (
        <div className="animate-fade-in space-y-6">
          {/* What Are Cookies */}
          {activeSection === 'what' && (
            <div>
              <SectionTitle>
                {t('cookiePolicy.sections.what.title')}
              </SectionTitle>
              <InfoBox>
                <p>{t('cookiePolicy.sections.what.description')}</p>
              </InfoBox>
            </div>
          )}

          {/* How We Use Cookies */}
          {activeSection === 'how' && (
            <div>
              <SectionTitle>
                {t('cookiePolicy.sections.how.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-5">
                {t('cookiePolicy.sections.how.description')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <FeatureCard
                  title={t('cookiePolicy.sections.how.improvingExperience')}
                >
                  {t('cookiePolicy.sections.how.improvingExperienceDesc')}
                </FeatureCard>
                <FeatureCard title={t('cookiePolicy.sections.how.performance')}>
                  {t('cookiePolicy.sections.how.performanceDesc')}
                </FeatureCard>
              </div>
              <div className="feature-card">
                <h3 className="text-white font-semibold mb-2">
                  {t('cookiePolicy.sections.how.toolsWeUse')}
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  {t('cookiePolicy.sections.how.toolsWeUseDesc')}
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="badge-teal text-xs">CookieYes</span>
                    <span>{t('cookiePolicy.sections.how.cookieYesDesc')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="badge-teal text-xs">Google Analytics</span>
                    <span>
                      {t('cookiePolicy.sections.how.googleAnalyticsDesc')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Types of Cookies */}
          {activeSection === 'types' && (
            <div>
              <SectionTitle>
                {t('cookiePolicy.sections.types.title')}
              </SectionTitle>
              <div className="space-y-4">
                <TypeCard
                  title={t('cookiePolicy.sections.types.essential')}
                  colorClass="text-blue-300"
                >
                  {t('cookiePolicy.sections.types.essentialDesc')}
                </TypeCard>
                <TypeCard
                  title={t('cookiePolicy.sections.types.preference')}
                  colorClass="text-green-300"
                >
                  {t('cookiePolicy.sections.types.preferenceDesc')}
                </TypeCard>
                <TypeCard
                  title={t('cookiePolicy.sections.types.analytics')}
                  colorClass="text-purple-300"
                  extra={
                    <div className="mt-3 bg-purple-900/20 rounded-lg p-3 border border-purple-500/20">
                      <h4 className="text-sm font-semibold text-white">
                        Google Analytics
                      </h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {t('cookiePolicy.sections.types.googleAnalyticsDetail')}
                      </p>
                    </div>
                  }
                >
                  {t('cookiePolicy.sections.types.analyticsDesc')}
                </TypeCard>
              </div>
            </div>
          )}

          {/* Managing Cookies */}
          {activeSection === 'managing' && (
            <div>
              <SectionTitle>
                {t('cookiePolicy.sections.managing.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-5">
                {t('cookiePolicy.sections.managing.description')}
              </p>
              <InfoBox>
                <h3 className="font-semibold text-indigo-200 text-sm mb-1">
                  {t('cookiePolicy.sections.managing.consentBanner')}
                </h3>
                <p className="text-sm">
                  {t('cookiePolicy.sections.managing.consentBannerDesc')}
                </p>
              </InfoBox>
              <div className="warning-box">
                <h3 className="font-semibold text-yellow-200 text-sm mb-1">
                  {t('cookiePolicy.sections.managing.browserSettings')}
                </h3>
                <p className="text-sm">
                  {t('cookiePolicy.sections.managing.browserSettingsDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Changes */}
          {activeSection === 'changes' && (
            <div>
              <SectionTitle>
                {t('cookiePolicy.sections.changes.title')}
              </SectionTitle>
              <p className="text-gray-400">
                {t('cookiePolicy.sections.changes.description')}
              </p>
              <div className="feature-card mt-5 flex items-start gap-4">
                <div className="bg-indigo-500/20 p-2 rounded-full flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-indigo-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-400">
                  {t('cookiePolicy.sections.changes.note')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </LegalPageLayout>
  );
};
